import { FunctionComponent, memo, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { getWfWorkflows, updateWfWorkflows } from "../services/WfWorkflowsService";
import { APP_CONFIGURATION_KEYS, CODE, RESPONSE_MESSAGE } from "../../utils/Constant";
import { toast } from "react-toastify";
import { WfWorkflowsNextStatuses, WfWorkflowsReq } from "../models/WfWorkflows";
import wfAxiosGenerator from "../utils/WfAxiosGenerator";
import { localStorageItem } from '../../utils/LocalStorage'
import { KEY_LOCALSTORAGE, RESPONSE_STATUS_CODE } from '../../auth/core/_consts'
import { ConfirmDialog } from "../../component/ConfirmDialog";
import SpinnerOverlay from "../../component/loading/SpinnerOverlay";
import CustomIconButton from "../../component/custom-icon-button/CustomIconButton";
import clsx from "clsx";
import { getErrorMessage } from "../../utils/FormatUtils";
import FormInPhieuDialog from "../../component/button-in-phieu/components/PrintDialog";
import { AxiosResponse } from "axios";
import { WORKFLOW_TYPE } from "../Constant";

interface WfAction {
  componentName: string,
  nameReq: string,
  cancel?: () => void
  submit: () => void,
  statusReq?: any
}
interface WfProps {
  tracker: string;
  objectId: string;
  additionalFunc?: (currStatusId?: string | undefined, codeButton?: string | undefined, listResponse?: any) => void;
  handleClick?: (props: WfAction) => void;
  additionalProps?: any;
  forceRender?: boolean;
  isContextMenu?: boolean;
  prefixFunction?: (
    currentStatusCode: string,
    wfCallbackFn: () => void | Promise<any>,
    cancel?: () => void,
    listResponse?: {}
  ) => void | Promise<any>;
  getTrackerField?: (fields: any) => void;
  componentName?: string;
  workflowList?: any;
  getCurrentWf?: (currStatus: any) => void;
  handleCloseMenu?: () => void;
  signatureParams?: any;
  setIsExistDisplayComponent?: (isExist: boolean) => void;
}

const BASE_ENDPOINT = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[APP_CONFIGURATION_KEYS.API_URL] || process.env.REACT_APP_API_URL || "";

const WfButton: FunctionComponent<WfProps> = memo((wfProps: WfProps) => {
  const [workflowList, setWorkflowList] = useState<any>({}); 
  const [notification, setNotification] = useState<string>("")
  const [loadingButton, setLoadingButton] = useState<{ new: boolean, old: boolean }>({ new: false, old: false });
  const [loadingAdditionalButton, setLoadingAdditionalButton] = useState<{ [key: string]: boolean }>({});
  const [openPrintDialog, setOpenPrintDialog] = useState<any>({
    open: false,
    printRequest: {} as (params: any) => Promise<AxiosResponse<Blob, any>>,
    params: {},
    hasSignature: false,
  });
  const printButtonRef = useRef<any>({});

  const ButtonComponent = wfProps?.isContextMenu ? "li" : Button;
  const showLoadingButton = (type: "new" | "old") => setLoadingButton(current => ({ ...current, [type]: true }));
  const hideLoadingButton = (type: "new" | "old") => setLoadingButton(current => ({ ...current, [type]: false }));
  const showLoadingAdditionalButton = (code: string) => setLoadingAdditionalButton(current => ({ ...current, [code]: true }));
  const hideLoadingAdditionalButton = (code: string) => setLoadingAdditionalButton(current => ({ ...current, [code]: false }));
  const currentAction = useRef<(statusReq: WfWorkflowsReq | undefined, codeReq: string, nameReq: string, componentName: string, type: "new" | "old", wfAxiosRequests: any, statusCode: any) => Promise<void> | undefined>();

  const updatePageData = useMemo(() => async () => {
    try {
      if (!wfProps?.workflowList) {
        let { data } = await getWfWorkflows(wfProps.tracker, wfProps.objectId);
        if (data?.code === CODE.SUCCESS) {
          wfProps?.getCurrentWf?.(data.data)
          setWorkflowList(data.data || []);
          checkOpenContextMenu(data.data);
        }
      } else {
        setWorkflowList(wfProps?.workflowList);
        checkOpenContextMenu(wfProps.workflowList);
      }
    } catch (e) {
      toast.warning(RESPONSE_MESSAGE.ERROR);
    }
  }, [wfProps.tracker, wfProps.objectId]);

  useEffect(() => {
    updatePageData();
  }, [wfProps.tracker, wfProps.objectId, wfProps?.forceRender, updatePageData, wfProps?.workflowList]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (printButtonRef.current?.[e.key]) {
      e.preventDefault();
      printButtonRef.current?.[e.key]?.click();
    }
  };

  const changeStatus = async (wfWorkflowsReq: WfWorkflowsReq | undefined, codeRequest?: string, listResponse?: any) => {
    if (!wfWorkflowsReq) return;
    try {
      let { data } = await updateWfWorkflows(wfProps.tracker, wfWorkflowsReq);

      if(data?.code === CODE.SUCCESS) {
        setWorkflowList(data.data || []);
        wfProps?.getCurrentWf?.(data.data)
        if (wfProps.additionalFunc) wfProps.additionalFunc(codeRequest, undefined, listResponse);
      }
      else {
        if (data?.message) {
          setNotification(data?.message || "");
        }
        return data;
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    } finally {
      hideLoadingButton("new");
      hideLoadingButton("old");
    }
  };

  const handleButtonClick = async (additionalButton: any, statusCode?: any) => {
    if (loadingAdditionalButton?.[additionalButton.code]) return;
    showLoadingAdditionalButton(additionalButton.code);
    try {
      const submit = async () => {
        let response = await additionalButton.handleClick(wfProps);
        wfProps.additionalFunc?.(statusCode?.curr, additionalButton.code, response);
        hideLoadingAdditionalButton(additionalButton.code);
      };

      if (additionalButton?.componentName && wfProps.handleClick) {
        wfProps.handleClick({
          componentName: additionalButton.componentName,
          nameReq: additionalButton.name,
          submit: submit,
          cancel: () => hideLoadingAdditionalButton(additionalButton.code),
          
        });
      } else {
        submit();
      }
    } catch (error) {
      console.error(error);
      toast.error("Đã có lỗi xảy ra!");
      hideLoadingAdditionalButton(additionalButton.code);
    }
  };

  const handleReq = async (statusReq: WfWorkflowsReq | undefined, codeReq: string, nameReq: string, componentName: string, type: "new" | "old", wfAxiosRequests: any, statusCode: any, callbackData?: any) => {
    if (loadingButton[type]) return;
    showLoadingButton(type);
    try {
      const actions =  wfAxiosRequests?.actions?.[codeReq];
      let listResponse: any = {}
      const submit = async (): Promise<void> => {
        const shouldExecutePrefixFirst = wfAxiosRequests?.priority === "status";
    
        const executeActions = async (): Promise<any> => {
            if (!actions) return;
            for (const action of actions) {
              // actions từ generateActions đã filter SERVICE_TASK rồi, chỉ còn functions
              if (typeof action !== 'function') continue;

              let data = await action(wfProps, callbackData, currentAction.current);
                if (data?.data?.code >= RESPONSE_STATUS_CODE.BAD_REQUEST) {
                    setNotification(getErrorMessage(data?.data));
                    hideLoadingButton(type);
                    return data;
                }
                listResponse = { ...data?.data?.data, ...listResponse };
            }
        };
    
        const executePrefixFunction = async (): Promise<any> => {
          if (wfProps.prefixFunction) {
            wfProps.prefixFunction(
              statusCode.curr,
              async () => {
                await changeStatus(statusReq, codeReq, listResponse);
                hideLoadingButton(type);
              },
              () => hideLoadingButton(type),
              listResponse
            );
            return; 
          }
          const statusData = await changeStatus(statusReq, codeReq, listResponse);
          return statusData;
        };
    
        if (shouldExecutePrefixFirst) {
          const statusResult = await executePrefixFunction()
          if (statusResult) return statusResult;
          const actionResult = await executeActions();
          if (actionResult) return actionResult;
        } else {
          const actionResult = await executeActions();
          if (actionResult) return actionResult;
          const statusResult = await executePrefixFunction()
          if (statusResult) return statusResult;
        }
        
        hideLoadingButton(type);
      };
    
      if (componentName && wfProps.handleClick) {
        wfProps.handleClick({
          componentName: componentName,
          nameReq: nameReq,
          submit: submit,
          cancel: () => hideLoadingButton(type),
          statusReq
        });
      } else {
        await submit();
      }
    } catch (error) {
      console.error(error);
      toast.error("Đã có lỗi xảy ra!");
      hideLoadingButton(type);
    }
  };

  const getWfAxiosRequests = (workflow: any) => {
    return wfAxiosGenerator(BASE_ENDPOINT, workflow.additionalConfiguration);
  }

  const checkOpenContextMenu = (workflowList: any) => {
    if (!workflowList?.nextStatuses?.length && wfProps?.isContextMenu && !(wfProps as WfProps & { additionalMenuItems: any[] })?.additionalMenuItems?.length) {
      wfProps?.handleCloseMenu?.();
    }
  };

  return (
    <>
      <>
        {workflowList?.additionalConfiguration 
          && wfProps.objectId 
          && getWfAxiosRequests(workflowList)?.axiosRequests
          && getWfAxiosRequests(workflowList)?.axiosRequests?.map((additionalButton: any) => {
            // SERVICE_TASK không render button, chạy ngầm tự động
            if (additionalButton.actionType === WORKFLOW_TYPE.SERVICE_TASK) {
              return null;
            }

            if ([WORKFLOW_TYPE.POPUP, WORKFLOW_TYPE.PRINT, WORKFLOW_TYPE.FETCH].includes(additionalButton.actionType)) {
              return (
                <ButtonComponent
                  ref={(ref: any) => {
                    printButtonRef.current = {
                      ...printButtonRef.current,
                      [additionalButton.keyShortcut]: ref,
                    };
                  }}
                  key={additionalButton.code}
                  className={clsx(
                    'position-relative',
                    'd-flex justify-content-between align-items-center',
                    'border-bottom drop-list-item',
                    {
                      'btn btn-fill': !wfProps.isContextMenu,
                      'p-1': wfProps.isContextMenu,
                    }
                  )}
                  onClick={async () => {
                    if (additionalButton.actionType === WORKFLOW_TYPE.PRINT) {
                      const buttonConfig = await additionalButton.handleClick(wfProps);
                      
                      setOpenPrintDialog({
                        open: true,
                        printRequest: buttonConfig.request,
                        params: buttonConfig.payload,
                        hasSignature: additionalButton.hasSignature,
                        keyShortcut: additionalButton.keyShortcut,
                      });
                      wfProps.setIsExistDisplayComponent?.(true);
                      return;
                    }
                    if (additionalButton.actionType === WORKFLOW_TYPE.POPUP) {
                      wfProps.handleClick?.({
                        componentName: additionalButton.code,
                        nameReq: additionalButton.iconText,
                        submit: () => {},
                        statusReq: { objectId: wfProps.objectId }
                      });
                      return;
                    }
                    if (additionalButton.actionType === WORKFLOW_TYPE.FETCH) {
                      handleButtonClick(additionalButton);
                      return;
                    }
                  }}
                >
                  {additionalButton.icon &&
                    <span className="fs-4 min-w-15px text-center">
                      <i className={`fs-16 ${additionalButton.icon}`} />
                    </span>
                  }
                  <div className="d-flex justify-content-between align-items-center w-100 ps-2">
                    <span>{additionalButton.iconText || additionalButton.name}</span>
                  </div>
                  {loadingAdditionalButton?.[additionalButton.code] && <SpinnerOverlay />}
                </ButtonComponent>
				      );
            }
            return (
              (
                !wfProps?.componentName || 
                additionalButton?.componentNames?.find((componentName: string) => componentName === wfProps?.componentName) || 
                !additionalButton?.componentNames) && 
                <ButtonComponent 
                  className={`position-relative min-w-80 spaces mx-3 btn btn-fill ${loadingAdditionalButton?.[additionalButton.code] ? "cursor-wait" : ""}`} 
                  key={additionalButton.code}  
                  onClick={() => handleButtonClick(additionalButton)}
                >
                  {loadingAdditionalButton?.[additionalButton.code] && <SpinnerOverlay />}
                  {additionalButton.icon && 
                    <span className="fs-4 min-w-15px text-center">
                      <i className={`fs-16 ${additionalButton.icon}`} />
                    </span>
                  }
                  {additionalButton.name}
                </ButtonComponent>
            )
        })}
        {
          Array.isArray(workflowList?.nextStatuses)&& workflowList?.nextStatuses?.map((workflow: WfWorkflowsNextStatuses) => {
            let wfAxiosRequests = getWfAxiosRequests(workflow)
            let wfWorkflowsReqOld = { objectId: wfProps.objectId, status: workflow.oldStatusId }
            let config = workflow.additionalConfiguration ? JSON.parse(workflow.additionalConfiguration) : {};
            let wfWorkflowsReqNew = { objectId: wfProps.objectId, status: workflow.newStatusId }
            let statusCode = { old: workflow.oldStatusCode, curr: workflow.currStatusCode, new: workflow.newStatusCode }
            let newStatusName = workflow.newStatusName
            let oldStatusName = workflow.oldStatusName
            let componentConfig = wfAxiosRequests?.components?.find?.((component: any) => component?.name === wfProps?.componentName) 
            let isHiddenComponent = wfAxiosRequests?.components?.find?.((component: any) => component?.name === "None");
            return (
              (componentConfig || !wfProps?.componentName || !wfAxiosRequests?.components)
              && !isHiddenComponent  
            )
            ? (
              <>
                {wfAxiosRequests?.axiosRequests &&
                  wfProps.objectId &&
                  wfAxiosRequests.axiosRequests.map((additionalButton: any) => {
                    // SERVICE_TASK trong additionalAction không render button, chạy ngầm
                    if (additionalButton.actionType === WORKFLOW_TYPE.SERVICE_TASK) {
                      return null;
                    }

                    return (
                      <ButtonComponent
                        className={`position-relative spaces mx-3 btn btn-fill ${
                          loadingAdditionalButton?.[additionalButton.code]
                            ? 'cursor-wait'
                            : ''
                        }`}
                        key={additionalButton.code}
                        onClick={() => handleButtonClick(additionalButton, statusCode)}
                      >
                        {loadingAdditionalButton?.[additionalButton.code] && (
                          <SpinnerOverlay />
                        )}
                        {additionalButton.name}
                      </ButtonComponent>
                    );
                  })}

                {workflow.oldStatusName && wfProps.objectId && (
                  <ButtonComponent
                    className={`position-relative spaces mx-3 btn btn-fill ${
                      loadingButton.old ? 'cursor-wait' : ''
                    }`}
                    id="wf-old-status-button"
                    onClick={() => {
                      let _handleReq = (data?: any) => handleReq(
                        wfWorkflowsReqOld,
                        workflow.oldStatusCode,
                        workflow.oldStatusName,
                        config?.componentNameOldReq,
                        'old',
                        wfAxiosRequests,
                        statusCode,
                        data
                      );
                      currentAction.current = _handleReq;
                      _handleReq();
                    }}
                  >
                    {loadingButton.old && <SpinnerOverlay />}
                    <span>
                      {config?.oldStatusName ? config?.oldStatusName : oldStatusName}
                    </span>
                  </ButtonComponent>
                )}

                {workflow.newStatusName && wfProps.objectId && (
                  <>
                    {!componentConfig?.icon && !componentConfig?.iconText && (
                      <ButtonComponent
                        className={clsx('position-relative spaces', {
                          'cursor-wait' : loadingButton.new,
                          'border-bottom drop-list-item py-4 pl-25': wfProps.isContextMenu,
                          'mx-3 btn btn-fill': !wfProps.isContextMenu,
                        })}
                        id="wf-new-status-button"
                        onClick={() => {
                          let _handleReq = (data?: any) => handleReq(
                            wfWorkflowsReqNew,
                            statusCode.new,
                            workflow.newStatusName,
                            wfAxiosRequests?.componentNameNewReq,
                            'new',
                            wfAxiosRequests,
                            statusCode,
                            data
                          );
                          currentAction.current = _handleReq;
                          _handleReq();
                        }}
                      >
                        {loadingButton.new && <SpinnerOverlay />}
                        <span>
                          { config?.newStatusName ? config?.newStatusName : newStatusName }
                        </span>
                      </ButtonComponent>
                    )}

                    {componentConfig?.icon && !componentConfig?.iconText && (
                      <CustomIconButton
                        variant="edit"
                        title={`${workflow?.newStatusName}`}
                        onClick={() =>
                          handleReq(
                            wfWorkflowsReqNew,
                            statusCode.new,
                            workflow.newStatusName,
                            wfAxiosRequests?.componentNameNewReq,
                            'new',
                            wfAxiosRequests,
                            statusCode
                          )
                        }
                      >
                        <i className={componentConfig?.icon}></i>
                      </CustomIconButton>
                    )}

                    {componentConfig?.iconText && (
                      <ButtonComponent
                        className={clsx(
                          'position-relative p-1',
                          'd-flex justify-content-between align-items-center',
                          'border-bottom drop-list-item',
                          {
                            'cursor-wait': loadingButton.new,
                          }
                        )}
                        id="wf-new-status-button"
                        onClick={() =>
                          handleReq(
                            wfWorkflowsReqNew,
                            statusCode.new,
                            workflow.newStatusName,
                            wfAxiosRequests?.componentNameNewReq,
                            'new',
                            wfAxiosRequests,
                            statusCode
                          )
                        }
                      >
                        {loadingButton.new && <SpinnerOverlay />}
                        <span className="fs-4 min-w-15px text-center">
                          <i
                            className={`fs-16 ${
                              componentConfig.icon
                            }`}
                          />
                        </span>
                        <div className="d-flex justify-content-between align-items-center w-100 ps-2">
                          <span>{componentConfig.iconText}</span>
                        </div>
                      </ButtonComponent>
                    )}
                  </>
                )}
              </>
            ) : (
              <></>
            );
                })
              }
      </>
      {
        <ConfirmDialog
          title="Thông báo"
          yes="Đóng"
          onYesClick={() => setNotification("")}
          message={notification}
          show={Boolean(notification)}
          onCloseClick={() => setNotification("")}
        />
      }
      {openPrintDialog.open && 
        <FormInPhieuDialog
          show={openPrintDialog.open}
          onHide={() => {
						setOpenPrintDialog({
							open: false,
							printRequest: null,
							params: null,
							hasSignature: false,
						});
            wfProps.setIsExistDisplayComponent?.(false);
					}}
          fetchExport={openPrintDialog.printRequest}
          params={openPrintDialog.params}
          hasSignature={openPrintDialog.hasSignature}
          signatureParams={wfProps.signatureParams}
        />
      }
    </>
  );
});

export default WfButton;
